import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { MousePointerClick, ScrollText, MousePointer2 } from 'lucide-react';
import { useHeatmap } from '@/hooks/use-heatmap';
import { HeatmapStage } from '@/components/heatmap/heatmap-stage';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type Range = '24h' | '7d' | '30d';
type HeatType = 'click' | 'scroll' | 'move';

export const Route = createFileRoute('/_authenticated/sites/$siteId/heatmap')({
  component: HeatmapPage,
});

function HeatmapPage() {
  const { siteId } = Route.useParams();
  const [url, setUrl] = useState('/');
  const [range, setRange] = useState<Range>('7d');
  const [heatType, setHeatType] = useState<HeatType>('click');

  const { data, isLoading } = useHeatmap(siteId, url, range);

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 animate-fade-up">
      <header className="mb-6 flex items-start justify-between gap-6">
        <div>
          <h1 className="mb-1 text-2xl font-semibold tracking-tight">Heatmap</h1>
          <p className="text-sm text-text-muted">
            {data?.total_events ?? 0} click events
            {data && ` · generated ${new Date(data.generated_at).toLocaleTimeString()}`}
          </p>
        </div>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-border bg-bg-surface p-0.5 gap-0.5">
          {(['click', 'scroll', 'move'] as HeatType[]).map((t) => (
            <button
              key={t}
              onClick={() => setHeatType(t)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded transition-colors',
                heatType === t
                  ? 'bg-bg-surface-3 text-text shadow-[0_0_0_1px_var(--border-strong)]'
                  : 'text-text-muted hover:text-text',
              )}
            >
              {t === 'click' && <MousePointerClick className="size-3" />}
              {t === 'scroll' && <ScrollText className="size-3" />}
              {t === 'move' && <MousePointer2 className="size-3" />}
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-md border border-border bg-bg-surface p-0.5 gap-0.5">
          {(['24h', '7d', '30d'] as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded transition-colors',
                range === r
                  ? 'bg-bg-surface-3 text-text shadow-[0_0_0_1px_var(--border-strong)]'
                  : 'text-text-muted hover:text-text',
              )}
            >
              {r}
            </button>
          ))}
        </div>
        <div className="ml-auto inline-flex items-center gap-2 rounded-md border border-border bg-bg-surface px-3 py-1.5 text-xs">
          URL: <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="bg-transparent outline-none font-mono text-text w-40"
            placeholder="/"
          />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="w-full" style={{ aspectRatio: '12/8' }} />
      ) : (
        <HeatmapStage points={data?.points ?? []} url={url} />
      )}
    </div>
  );
}
